import { Alert, Checkbox, DatePicker, Form, Input, Modal } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import {
  ReturnRow, earliestOutstationPickup, isPickupDayDisabled, pickupCutoffPassed, rescheduleReturnPickup,
} from "../returns.api";

interface Props {
  row: ReturnRow | null;
  reattempt?: boolean;
  onClose: () => void;
  onRescheduled: () => void;
}

const ReschedulePickupModal: React.FC<Props> = ({ row, reattempt = false, onClose, onRescheduled }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const allowToday = Form.useWatch("allow_today", form) === true;
  const outstationFloor = row?.delivery_city === "outstation" ? earliestOutstationPickup() : null;

  useEffect(() => {
    if (!row) return;
    form.resetFields();
    if (reattempt) {
      form.setFieldsValue({
        pickup_date: row.delivery_city === "outstation" ? earliestOutstationPickup() : dayjs(),
      });
    }
  }, [row, reattempt, form]);

  const handleOk = async () => {
    if (!row) return;
    const values = await form.validateFields();
    setSaving(true);
    const ok = await rescheduleReturnPickup(row.stage_id, {
      pickup_date: values.pickup_date.format("YYYY-MM-DD"),
      reason: values.reason?.trim() || undefined,
      reattempt: reattempt || undefined,
      allow_today: values.allow_today || undefined,
    });
    setSaving(false);
    if (ok) {
      onRescheduled();
      onClose();
    }
  };

  return (
    <Modal
      title={`${reattempt ? "Reattempt" : "Reschedule"} pickup — return #R-${row?.stage_id ?? ""}`}
      open={row !== null}
      onCancel={onClose}
      onOk={handleOk}
      okText={reattempt ? "Reattempt" : "Reschedule"}
      confirmLoading={saving}
      destroyOnClose
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={reattempt && row?.status === "picked_up"
            ? "The driver's pickup will be cleared and this return re-booked on its own truck."
            : reattempt
              ? "The pickup truck will be re-booked for another attempt."
              : "The pickup truck will be re-booked for the new date."}
          description={reattempt && row?.status === "picked_up"
            ? "Other returns on the old truck stay on it. Sundays and holidays roll to the next working day."
            : "Other returns riding the same truck move with it. Sundays and holidays roll to the next working day."}
        />
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item label="Current pickup date">
            {row?.pickup_date ? dayjs(row.pickup_date).format("DD MMM YYYY") : "—"}
          </Form.Item>
          <Form.Item
            label={reattempt ? "Attempt on" : "New pickup date"}
            name="pickup_date"
            rules={[{ required: true, message: "Pick a date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD MMM YYYY"
              disabledDate={(d) => outstationFloor
                ? d.isBefore(outstationFloor, "day")
                : reattempt ? d.isBefore(dayjs(), "day") : isPickupDayDisabled(d, allowToday)}
            />
          </Form.Item>
          {outstationFloor && (
            <Alert type="warning" showIcon style={{ marginTop: -8, marginBottom: 16 }}
                   message={`Outstation customer — earliest pickup is D+3 (${outstationFloor.format("DD MMM")}).`} />
          )}
          {!reattempt && !outstationFloor && pickupCutoffPassed() && (
            <Form.Item name="allow_today" valuePropName="checked" style={{ marginTop: -8 }}>
              <Checkbox>Allow today (escalated)</Checkbox>
            </Form.Item>
          )}
          <Form.Item label="Reason (optional)" name="reason">
            <Input.TextArea
              rows={2}
              maxLength={500}
              placeholder={reattempt ? "e.g. customer was not home on the first attempt" : "e.g. customer not available"}
            />
          </Form.Item>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ReschedulePickupModal;
